import React from "react";
import { ClipboardDocumentIcon } from "@heroicons/react/24/outline";

/**
 * Compact label-row copy control for one result-field value : the one
 * implementation of the button that CSRDecoder, CertificateMatcher,
 * and DCVChecker each previously carried by hand (2026-08-07
 * download/copy consistency pass, operator-directed - three identical
 * copies meant three files to touch for any styling change).
 *
 * The aria-label interpolates the field label so screen readers can
 * tell a card's otherwise identically-named Copy buttons apart ; the
 * chain diagram's per-certificate aria-label is the house precedent.
 * The aria-label MUST flip with the visible state : an aria-label
 * overrides text content when computing the accessible name, so a
 * static label would permanently mask the "Copied!" confirmation from
 * assistive tech while sighted customers see it (the icon buttons on
 * PasswordGenerator / CAARecordGenerator flip theirs the same way).
 * Pair this component with the useCopyFeedback hook, whose
 * success-gated status guarantees a failed copy never shows the
 * confirmation.
 *
 * Only the button lives here : the field WRAPPERS deliberately stay
 * per-page (CSRDecoder's inset bg-gray-50 card, CertificateMatcher's
 * plain field, DCVChecker's colored Found Value box differ by
 * design).
 */
export default function CopyValueButton({
  label,
  value,
  fieldKey,
  copyStatus,
  onCopy,
}) {
  // The value gate lives HERE, once, for every consumer : a falsy
  // value means there is nothing transcribable (an absent field, an
  // "N/A" placeholder), and rendering the button anyway would put the
  // literal string "undefined" on the clipboard if a caller's
  // guarantee ever weakened. Callers may still ADD stricter gates of
  // their own (DCVChecker withholds the button for truncated values,
  // which are truthy but not transcribable).
  if (!value) return null;

  return (
    <button
      type="button"
      onClick={() => onCopy(value, fieldKey)}
      aria-label={copyStatus[fieldKey] ? "Copied!" : `Copy ${label}`}
      className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-control rounded-sm text-body hover:bg-gray-100 dark:hover:bg-gray-700 shrink-0"
    >
      <ClipboardDocumentIcon className="w-3.5 h-3.5" />
      {copyStatus[fieldKey] ? "Copied!" : "Copy"}
    </button>
  );
}
